import React, {useState, useContext} from 'react';
import constants from 'client/constants';
import {
  Button,
  Dropdown,
  DropdownItem,
  Icon,
  copyToClipboard,
  addToast,
  Dialogue
} from '@albert-io/atomic';
import {history} from 'client/history';
import {invalidatePartialInterest, genericMandarkRequest} from 'resources/mandark.resource';
import ConfirmActionModal from 'client/components/ConfirmActionModal/ConfirmActionModal.react';
import {CreateAssignmentContext} from 'client/CreateAssignmentV2/CreateAssignmentProvider';
import {getQuestionSetsQuery} from 'client/CreateAssignmentV2/utils/createAssignment.queries';
import {ReportsContext} from 'client/Reports/Reports.context';

import './assignment-actions.scss';

interface Props {
  assignment: any;
}

const AssignmentActions = ({assignment}: Props) => {
  const assignmentId = assignment?.id;
  const [showModal, setShowModal] = useState(false);

  const {bootstrapAssignment} = useContext<any>(CreateAssignmentContext);
  const {fetchData} = useContext(ReportsContext);

  // on the all classes tab, this just keys into the first classroom assignment
  // on a specific classroom, it will already be filtered by the classroom_id
  const classroomAssignment = (assignment?.classroom_assignments || [])?.[0];

  const copyShareLink = () => {
    copyToClipboard(`${constants.ALBERT_FULL_PATH}/assignment/${assignmentId}`, () => {
      addToast({
        type: 'success',
        title: 'Link Copied!',
        message: 'The link to this assignment has been copied to your clipboard.'
      });
    });
  };

  const handleReassign = () => {
    const query = getQuestionSetsQuery('assignments_v3', assignmentId);
    bootstrapAssignment({
      query,
      resourceType: 'assignments_v3',
      assignmentOrigin: assignment.name,
      assignmentName: `${assignment.name} (Reassigned)`,
      resourceId: assignment.id
    });
    history.pushState(null, `/create-assignment/new-assignment`);
  };

  const handleDeleteAssignment = async () => {
    const query = {resourcePath: ['assignments_v3', assignment.id]};
    await genericMandarkRequest('delete', query);
    fetchData(undefined);
  };

  if (!assignmentId || !classroomAssignment) {
    return null;
  }

  const {classroom_id: classroomId} = classroomAssignment;

  return (
    <>
      {showModal && (
        <ConfirmActionModal
          handleClose={() => setShowModal(false)}
          title='Are you sure?'
          size='s'
          makeRequest={handleDeleteAssignment}
          hideCloseBtn
          onResolve={() => {
            invalidatePartialInterest({resourcePath: ['assignments_v3']});
          }}
        >
          {({isRequestInflight, onCancel, onConfirm}) => (
            <>
              <Dialogue.Body align='left'>
                <p>
                  You are about to delete <strong>{assignment.name}</strong>. This action cannot be
                  undone.
                </p>
              </Dialogue.Body>
              <Dialogue.BtnGroup align='right' className='u-display_inline'>
                <Button color='secondary' disabled={isRequestInflight} onClick={onCancel}>
                  Cancel
                </Button>
                <Button color='destructive' disabled={isRequestInflight} onClick={onConfirm}>
                  Delete
                </Button>
              </Dialogue.BtnGroup>
            </>
          )}
        </ConfirmActionModal>
      )}
      <Dropdown
        trigger={
          <Button variant='text'>
            Actions
            <Icon icon='caret-down' className='u-mar-l_1' />
          </Button>
        }
        positionFixed
        trayClassName='assignment-actions__dropdown-tray'
      >
        <DropdownItem as='button' onClick={handleReassign} icon='redo-alt'>
          Reassign
        </DropdownItem>
        <DropdownItem
          as='button'
          icon={['far', 'cog']}
          onClick={() => {
            history.pushState(
              null,
              `/create-assignment/assignment/${assignmentId}/classroom/${classroomId}/edit`
            );
          }}
          data-testid='assignment-actions__edit-btn'
        >
          Edit
        </DropdownItem>
        <DropdownItem as='button' onClick={copyShareLink} icon='link'>
          Share
        </DropdownItem>
        <DropdownItem
          as='button'
          icon={['far', 'trash-alt']}
          className='u-color_red-500'
          textProps={{color: 'red'}}
          iconColor='danger'
          onClick={() => {
            setShowModal(true);
          }}
        >
          Delete
        </DropdownItem>
      </Dropdown>
    </>
  );
};

export default AssignmentActions;
